import React, { useState, useEffect, useRef } from "react";
import { Briefcase, ShieldCheck, CalendarDays, Star, Upload, Camera, ChevronRight, ChevronLeft, CheckCircle2, LayoutDashboard, Wallet, Inbox } from "lucide-react";
import { ccInput, ccLabel, ccCard, ccBtnPink, ccBtnBlue, ccBtnGhost, Stars, Avatar, EmptyState } from "./ui";
import { SERVICE_TYPES, VERIFICATION_DISCLAIMER } from "./careData";
import FocalPointModal from "../lib/FocalPoint";
import { toast } from "sonner";

// Uploads a verification document (government ID / credential proof) — starts unverified, pending master review
const uploadVerificationDoc = async (file, docType, credentialType = "") => {
  if (!file) return null;
  if (file.size > 4_500_000) {
    toast.error("File too large — keep it under ~4MB.");
    return null;
  }
  const dataUrl = await new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(file);
  });
  try {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/caregiver/verification-docs`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ doc_type: docType, credential_type: credentialType, file_name: file.name, data_base64: dataUrl }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      toast.error(data.detail || "Document upload failed.");
      return null;
    }
    toast.success("Document submitted — pending master review.");
    return data;
  } catch {
    toast.error("Network error — document upload failed.");
    return null;
  }
};

// ---------- Caregiving Hub (sub-tabs) ----------
export const CaregivingHub = ({ ctx }) => {
  const [tab, setTab] = useState("dashboard");
  const tabs = [
    { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    { key: "profile", label: "My Profile", icon: Briefcase },
    { key: "wizard", label: ctx.myCaregiverProfile ? "Edit Profile" : "Become a Caregiver", icon: Camera },
    { key: "verification", label: "Verification", icon: ShieldCheck },
    { key: "availability", label: "Availability", icon: CalendarDays },
    { key: "reviews", label: "Reviews", icon: Star },
  ];

  return (
    <div data-testid="cc-caregiving">
      <div className="flex gap-2 mb-6 overflow-x-auto" data-testid="cc-caregiving-tabs">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`cc-tab-${t.key}`}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-all duration-200 ${
              tab === t.key ? "bg-[#B3E0F2] text-[#374151] shadow-sm" : "text-[#9CA3AF] hover:text-[#374151] hover:bg-white dark:hover:bg-[#273244]"
            }`}
          >
            <t.icon className="w-3.5 h-3.5" />
            {t.label}
          </button>
        ))}
      </div>
      {tab === "dashboard" && <CaregiverDashboard ctx={ctx} onGoTab={setTab} />}
      {tab === "profile" && <MyProfile ctx={ctx} onEdit={() => setTab("wizard")} />}
      {tab === "wizard" && <BecomeCaregiverWizard ctx={ctx} onDone={() => setTab("profile")} />}
      {tab === "verification" && <VerificationCenter ctx={ctx} />}
      {tab === "availability" && <AvailabilityCalendar ctx={ctx} />}
      {tab === "reviews" && <ReviewsScreen ctx={ctx} />}
    </div>
  );
};

// ---------- My Profile ----------
const MyProfile = ({ ctx, onEdit }) => {
  const p = ctx.myCaregiverProfile;
  if (!p)
    return (
      <EmptyState text="You don't have a caregiver profile yet. Use 'Become a Caregiver' to create one." testId="cc-myprofile-empty" />
    );
  return (
    <div className={`${ccCard} p-8 max-w-2xl animate-fade-up`} data-testid="cc-myprofile">
      <div className="flex items-start gap-5">
        <Avatar src={p.photo} name={p.name} size="w-20 h-20" />
        <div>
          <h2 className="text-xl font-black font-heading text-[#374151] dark:text-slate-100">{p.name}</h2>
          <p className="text-xs text-[#9CA3AF] font-medium">{p.city} · within {p.radiusMiles} miles</p>
          <span className="inline-block mt-2 px-3 py-1 bg-[#FFF2D1] border border-[#F5E3B3] text-[#7A5A17] text-[10px] font-extrabold uppercase rounded-full">
            Pending Verification Review
          </span>
        </div>
      </div>
      <p className="text-sm text-[#6B7280] mt-4 dark:text-slate-300">{p.bio}</p>
      <div className="flex gap-1.5 mt-4 flex-wrap">
        {p.services.map((s) => (
          <span key={s} className="px-2.5 py-1 bg-[#E6F2FA] border border-[#B3E0F2] text-[#3D7E9E] text-[11px] font-bold rounded-full">
            {s}
          </span>
        ))}
      </div>
      <p className="text-sm font-extrabold text-[#374151] mt-4 dark:text-slate-100">
        ${p.hourlyRate}–${p.rateMax}/hour · {p.yearsExperience} years experience
      </p>
      <button onClick={onEdit} className={`${ccBtnPink} mt-6`} data-testid="cc-edit-profile-btn">
        Edit Profile
      </button>
    </div>
  );
};

// ---------- Hire opt-in (real caregiver accounts list themselves to facilities) ----------
const HireOptInCard = ({ ctx }) => {
  const me = ctx.currentUser;
  const [open, setOpen] = useState(null);
  const [headline, setHeadline] = useState("");

  useEffect(() => {
    if (!me?.real || me.role !== "caregiver") return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((u) => {
        if (u) {
          setOpen(!!u.open_to_hire);
          setHeadline(u.hire_headline || "");
        }
      })
      .catch((err) => console.debug("hire status fetch skipped", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.real, me?.role]);

  if (!me?.real || me.role !== "caregiver") return null;
  if (open === null) return null;

  const save = async (next) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me/hire-status`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ open_to_hire: next, headline }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Could not update");
      setOpen(next);
      toast.success(next ? "You're now listed for facility hire!" : "Removed from the hire list.");
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className={`${ccCard} p-6`} data-testid="cc-hire-optin">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-black font-heading text-[#374151] dark:text-slate-100">List Me for Facility Hire</h3>
          <p className="text-xs text-[#9CA3AF] font-medium mt-1">
            Daycare operators can discover and hire you directly from their dashboard.
          </p>
        </div>
        <button
          onClick={() => save(!open)}
          data-testid="cc-hire-toggle"
          className={`px-5 py-2.5 rounded-full text-xs font-bold transition-all shadow-sm ${open ? "bg-[#E2F4E1] border border-[#C4E5C2] text-[#2F6031]" : "bg-[#F8C8DC] hover:bg-[#f3b4cd] text-[#374151]"}`}
        >
          {open ? "Listed ✓ — Click to Unlist" : "List Me for Hire"}
        </button>
      </div>
      {open && (
        <input
          type="text"
          value={headline}
          onChange={(e) => setHeadline(e.target.value)}
          onBlur={() => save(true)}
          placeholder="Headline facilities see — e.g. CPR-certified toddler specialist, evenings"
          className={`${ccInput} mt-3`}
          data-testid="cc-hire-headline"
        />
      )}
    </div>
  );
};

// ---------- Caregiver Dashboard (earnings, requests, availability sync) ----------
const parseHour = (t) => {
  const m = /(\d+):(\d+)\s*(AM|PM)/i.exec(t || "");
  if (!m) return null;
  let h = parseInt(m[1], 10) % 12;
  if (m[3].toUpperCase() === "PM") h += 12;
  return h + parseInt(m[2], 10) / 60;
};

const requestHours = (r) => {
  const s = parseHour(r.startTime);
  const e = parseHour(r.endTime);
  if (s === null || e === null || e <= s) return 0;
  return Math.round((e - s) * 10) / 10;
};

const CaregiverDashboard = ({ ctx, onGoTab }) => {
  const myCgId = ctx.myCaregiverProfile?.id || (ctx.viewUser.role === "caregiver" ? ctx.viewUser.caregiverId : null);
  if (!myCgId)
    return <EmptyState text="Publish your caregiver profile (Become a Caregiver) to unlock your workspace." testId="cc-dash-empty" />;

  const rate = ctx.myCaregiverProfile?.hourlyRate || ctx.caregivers.find((c) => c.id === myCgId)?.hourlyRate || 0;
  const mine = ctx.requests.filter((r) => r.caregiverId === myCgId);
  const pending = mine.filter((r) => r.status === "sent");
  const confirmed = mine.filter((r) => r.status === "connected" || r.status === "confirmed");
  const completed = mine.filter((r) => r.status === "completed" || r.status === "reviewed");
  const earnings = Math.round(completed.reduce((sum, r) => sum + requestHours(r) * rate, 0) * 100) / 100;
  const projected = Math.round(confirmed.reduce((sum, r) => sum + requestHours(r) * rate, 0) * 100) / 100;
  const myReviews = ctx.reviews.filter((r) => r.caregiverId === myCgId);
  const avgRating = myReviews.length ? (myReviews.reduce((s, r) => s + r.rating, 0) / myReviews.length).toFixed(1) : null;
  const availableDays = Object.values(ctx.availabilityDays).filter((v) => v === "available").length;

  const stats = [
    { label: "Earnings (completed)", value: `$${earnings.toFixed(2)}`, icon: Wallet, testId: "cc-dash-earnings" },
    { label: "Projected (booked)", value: `$${projected.toFixed(2)}`, icon: Briefcase, testId: "cc-dash-projected" },
    { label: "Pending requests", value: pending.length, icon: Inbox, testId: "cc-dash-pending" },
    { label: "Avg rating", value: avgRating ? `${avgRating} ★` : "—", icon: Star, testId: "cc-dash-rating" },
  ];

  return (
    <div className="space-y-6" data-testid="cc-caregiver-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <div key={s.label} className={`${ccCard} p-5`} data-testid={s.testId}>
            <s.icon className="w-5 h-5 text-[#E5A0BC] mb-2" />
            <p className="text-xl font-black font-heading text-[#374151] dark:text-slate-100">{s.value}</p>
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[#9CA3AF] mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      <div className={`${ccCard} p-6`}>
        <h3 className="font-black font-heading text-[#374151] dark:text-slate-100 mb-1">Incoming Care Requests</h3>
        <p className="text-xs text-[#9CA3AF] font-medium mb-4">Accept to connect with the family, or decline to free the slot.</p>
        {pending.length === 0 ? (
          <p className="text-sm font-bold text-[#9CA3AF] py-4 text-center" data-testid="cc-dash-no-pending">No pending requests right now.</p>
        ) : (
          <div className="space-y-3">
            {pending.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 flex-wrap bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl p-4 dark:bg-[#273244] dark:border-[#374457]" data-testid={`cc-dash-req-${r.id}`}>
                <div>
                  <p className="text-sm font-black text-[#374151] dark:text-slate-100">{r.familyName} — {r.service}</p>
                  <p className="text-[11px] text-[#9CA3AF] font-bold">
                    {r.date} · {r.startTime}–{r.endTime} · {requestHours(r)}h{rate ? ` · est. $${(requestHours(r) * rate).toFixed(2)}` : ""}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => ctx.respondRequest(r.id, true)} data-testid={`cc-dash-accept-${r.id}`} className={ccBtnPink}>Accept</button>
                  <button onClick={() => ctx.respondRequest(r.id, false)} data-testid={`cc-dash-decline-${r.id}`} className={ccBtnGhost}>Decline</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={`${ccCard} p-6`}>
        <h3 className="font-black font-heading text-[#374151] dark:text-slate-100 mb-4">Upcoming Booked Jobs</h3>
        {confirmed.length === 0 ? (
          <p className="text-sm font-bold text-[#9CA3AF] py-4 text-center" data-testid="cc-dash-no-jobs">Nothing booked yet — accepted requests appear here.</p>
        ) : (
          <div className="space-y-3">
            {confirmed.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 flex-wrap bg-[#E6F2FA]/60 border border-[#B3E0F2] rounded-2xl p-4 dark:bg-[#273244] dark:border-[#374457]" data-testid={`cc-dash-job-${r.id}`}>
                <div>
                  <p className="text-sm font-black text-[#374151] dark:text-slate-100">{r.familyName} — {r.service}</p>
                  <p className="text-[11px] text-[#3D7E9E] font-bold">{r.date} · {r.startTime}–{r.endTime}</p>
                </div>
                <button onClick={() => ctx.advanceRequest(r.id, "completed")} data-testid={`cc-dash-complete-${r.id}`} className={ccBtnBlue}>
                  <CheckCircle2 className="w-3.5 h-3.5 inline mr-1 -mt-0.5" /> Mark Completed
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <HireOptInCard ctx={ctx} />

      <div className={`${ccCard} p-6 flex items-center justify-between gap-3 flex-wrap`} data-testid="cc-dash-availability-sync">
        <div>
          <h3 className="font-black font-heading text-[#374151] dark:text-slate-100">Availability Sync</h3>
          <p className="text-xs text-[#9CA3AF] font-medium mt-1">
            {availableDays} day{availableDays === 1 ? "" : "s"} marked available this month — kept in sync with family search results.
          </p>
        </div>
        <button onClick={() => onGoTab("availability")} data-testid="cc-dash-manage-availability" className={ccBtnBlue}>
          <CalendarDays className="w-3.5 h-3.5 inline mr-1 -mt-0.5" /> Manage Availability
        </button>
      </div>
    </div>
  );
};

// Invited caregivers get their assigned type pre-selected (services + title)
const TYPE_TO_SERVICES = {
  Babysitter: ["Babysitting"],
  Nanny: ["Childcare"],
  "Respite Care": ["Respite Care"],
  "Special Needs Support": ["Special Needs Support"],
  "After-School Care": ["After-School Care"],
  "Newborn & Infant Care": ["Childcare"],
  "Tutor / Learning Support": ["Other"],
  "Date-Night Sitter": ["Babysitting", "Evening Care"],
  Other: ["Other"],
};

// ---------- Become a Caregiver Wizard (8 steps) ----------
const CREDENTIAL_OPTIONS = ["CPR", "First Aid", "CNA", "Respite Certification", "Childcare Certification", "Other Professional Credential"];
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const BecomeCaregiverWizard = ({ ctx, onDone }) => {
  const existing = ctx.myCaregiverProfile;
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    name: existing?.name || "",
    photo: existing?.photo || ctx.currentUser?.avatar || null,
    bio: existing?.bio || "",
    phone: existing?.phone || "",
    email: existing?.email || "",
    city: existing?.city || "",
    zip: existing?.zip || "",
    services: existing?.services || TYPE_TO_SERVICES[ctx.currentUser?.caregiver_type] || [],
    categories: existing?.categories || [],
    yearsExperience: existing?.yearsExperience || 1,
    experienceDetails: existing?.experienceDetails || "",
    skills: existing?.skills || "",
    credentials: existing?.credentials || [],
    availability: existing?.availability || {},
    radiusMiles: existing?.radiusMiles || 15,
    hourlyRate: existing?.hourlyRate || 20,
    rateMax: existing?.rateMax || 25,
    title: existing?.title || ctx.currentUser?.caregiver_type || "Caregiver",
  });

  const STEPS = ["Your Information", "Services You Offer", "Experience", "Credentials", "Availability", "Service Area", "Rate", "Profile Preview"];

  const toggleList = (key, val) =>
    setForm((f) => ({ ...f, [key]: f[key].includes(val) ? f[key].filter((x) => x !== val) : [...f[key], val] }));

  const rawPhotoRef = useRef(null);
  const [focalSrc, setFocalSrc] = useState(null);

  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      rawPhotoRef.current = reader.result;
      setFocalSrc(reader.result); // focus-point picker opens before the photo is saved
    };
    reader.readAsDataURL(file);
  };

  // Square-crop around the chosen focus point, downscaled to 512px so it can sync to the account avatar (server cap ~1.5MB)
  const applyFocalCrop = (point) => {
    const src = focalSrc;
    setFocalSrc(null);
    if (!src) return;
    const img = new Image();
    img.onload = () => {
      const side = Math.min(img.width, img.height);
      const cx = (point.x / 100) * img.width;
      const cy = (point.y / 100) * img.height;
      const left = Math.min(Math.max(cx - side / 2, 0), img.width - side);
      const top = Math.min(Math.max(cy - side / 2, 0), img.height - side);
      const canvas = document.createElement("canvas");
      canvas.width = 512;
      canvas.height = 512;
      canvas.getContext("2d").drawImage(img, left, top, side, side, 0, 0, 512, 512);
      setForm((f) => ({ ...f, photo: canvas.toDataURL("image/jpeg", 0.85) }));
    };
    img.onerror = () => setForm((f) => ({ ...f, photo: src }));
    img.src = src;
  };

  const validateBasics = () => {
    if (!form.name.trim()) {
      toast.error("Please enter your name before continuing.");
      return false;
    }
    if (form.services.length === 0) {
      toast.error("Select at least one service you offer.");
      return false;
    }
    return true;
  };

  const publish = () => {
    if (!validateBasics()) return;
    ctx.publishCaregiver(form);
    // One upload, everywhere: the caregiver photo is inherited by the regular account profile
    if (form.photo && form.photo !== ctx.currentUser?.avatar) ctx.onAvatarSync?.(form.photo);
    onDone();
  };

  const chipCls = (active) =>
    `px-3.5 py-2 rounded-full text-xs font-bold border transition-all duration-200 ${
      active
        ? "bg-[#B3E0F2] border-[#9ACCE0] text-[#374151] shadow-sm"
        : "bg-[#FFF9F2] border-[#FCE6E9] text-[#6B7280] hover:border-[#B3E0F2] dark:bg-[#273244] dark:border-[#374457] dark:text-slate-300"
    }`;

  return (
    <div className={`${ccCard} p-8 max-w-2xl animate-fade-up`} data-testid="cc-wizard">
      <h2 className="text-xl font-black font-heading text-[#374151] dark:text-slate-100">
        {existing ? "Edit Caregiver Profile" : "Become a Caregiver"}
      </h2>
      <p className="text-xs text-[#9CA3AF] font-medium mt-1 mb-5">
        Have experience providing childcare, respite, personal care or companion services? Create a professional profile and
        connect with families looking for help.
      </p>

      {/* Progress */}
      <div className="flex items-center gap-1.5 mb-6" data-testid="cc-wiz-progress">
        {STEPS.map((s, i) => (
          <div key={s} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-[#F8C8DC]" : "bg-[#FCE6E9] dark:bg-[#374457]"}`} />
        ))}
      </div>
      <p className="text-[11px] font-extrabold uppercase tracking-wider text-[#E5A0BC] mb-4" data-testid="cc-wiz-step">
        Step {step + 1} of 8 — {STEPS[step]}
      </p>

      {focalSrc && <FocalPointModal src={focalSrc} onDone={applyFocalCrop} onCancel={() => setFocalSrc(null)} />}

      {step === 0 && (
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => rawPhotoRef.current && setFocalSrc(rawPhotoRef.current)}
              title={rawPhotoRef.current ? "Tap to re-frame your photo" : "Upload a photo first"}
              data-testid="cc-wiz-photo-preview"
              className="w-16 h-16 rounded-full bg-[#FCE6E9] border-2 border-white shadow-sm flex items-center justify-center overflow-hidden shrink-0 cursor-pointer hover:border-[#F8C8DC] transition-colors"
            >
              {form.photo ? <img src={form.photo} alt="preview" className="w-full h-full object-cover" /> : <Camera className="w-5 h-5 text-[#E5A0BC]" />}
            </button>
            <div>
              <label className={`${ccBtnGhost} cursor-pointer inline-flex items-center gap-2`} data-testid="cc-wiz-photo-btn">
                <Upload className="w-4 h-4 text-[#7CC3E5]" /> Photo
                <input type="file" accept="image/*" onChange={handlePhoto} className="hidden" />
              </label>
              <p className="text-[10px] text-[#9CA3AF] font-semibold mt-1.5">After picking a photo, tap your face to set the focus — the crop centers on it everywhere.</p>
            </div>
          </div>
          <div>
            <label className={ccLabel}>Name</label>
            <input required type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={ccInput} data-testid="cc-wiz-name" />
          </div>
          <div>
            <label className={ccLabel}>Short Bio</label>
            <textarea rows={3} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className={ccInput} data-testid="cc-wiz-bio" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={ccLabel}>Phone</label>
              <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={ccInput} data-testid="cc-wiz-phone" />
            </div>
            <div>
              <label className={ccLabel}>Email</label>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={ccInput} data-testid="cc-wiz-email" />
            </div>
          </div>
          <div>
            <label className={ccLabel}>City / ZIP</label>
            <div className="grid grid-cols-2 gap-4">
              <input type="text" placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={ccInput} data-testid="cc-wiz-city" />
              <input type="text" placeholder="ZIP" value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} className={ccInput} data-testid="cc-wiz-zip" />
            </div>
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="space-y-4">
          {!existing && ctx.currentUser?.caregiver_type && (
            <p className="text-[11px] font-bold text-[#3D7E9E] bg-[#E6F2FA]/60 border border-[#B3E0F2] rounded-xl px-3 py-2" data-testid="cc-wiz-preselect-note">
              Pre-selected from your enrollment invite ({ctx.currentUser.caregiver_type}) — adjust as needed.
            </p>
          )}
          <label className={ccLabel}>Services You Offer</label>
          <div className="flex gap-2 flex-wrap" data-testid="cc-wiz-services">
            {SERVICE_TYPES.map((s) => (
              <button key={s} type="button" onClick={() => toggleList("services", s)} className={chipCls(form.services.includes(s))} data-testid={`cc-wiz-service-${s.replace(/\W/g, "-")}`}>
                {s}
              </button>
            ))}
          </div>
          <label className={ccLabel}>Caregiver Categories</label>
          <div className="flex gap-2 flex-wrap" data-testid="cc-wiz-categories">
            {ctx.categories.map((c) => (
              <button key={c} type="button" onClick={() => toggleList("categories", c)} className={chipCls(form.categories.includes(c))} data-testid={`cc-wiz-category-${c.replace(/\W/g, "-")}`}>
                {c}
              </button>
            ))}
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <div>
            <label className={ccLabel}>Years of Experience</label>
            <input type="number" min="0" value={form.yearsExperience} onChange={(e) => setForm({ ...form, yearsExperience: parseInt(e.target.value) || 0 })} className={ccInput} data-testid="cc-wiz-years" />
          </div>
          <div>
            <label className={ccLabel}>Previous Experience</label>
            <textarea rows={3} value={form.experienceDetails} onChange={(e) => setForm({ ...form, experienceDetails: e.target.value })} className={ccInput} data-testid="cc-wiz-experience" />
          </div>
          <div>
            <label className={ccLabel}>Special Skills</label>
            <input type="text" value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} className={ccInput} placeholder="e.g. sign language, behavioral support" data-testid="cc-wiz-skills" />
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <label className={ccLabel}>Credentials (select all that apply)</label>
          <div className="flex gap-2 flex-wrap" data-testid="cc-wiz-credentials">
            {CREDENTIAL_OPTIONS.map((c) => {
              const active = form.credentials.some((x) => x.type === c);
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() =>
                    setForm((f) => ({
                      ...f,
                      credentials: active ? f.credentials.filter((x) => x.type !== c) : [...f.credentials, { type: c, verified: false }],
                    }))
                  }
                  className={chipCls(active)}
                  data-testid={`cc-wiz-credential-${c.replace(/\W/g, "-")}`}
                >
                  {c}
                </button>
              );
            })}
          </div>
          {form.credentials.length > 0 && (
            <div className="space-y-2" data-testid="cc-wiz-cred-docs">
              {form.credentials.map((c) => (
                <div key={c.type} className="flex items-center justify-between gap-3 bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl px-4 py-2.5 dark:bg-[#273244] dark:border-[#374457]">
                  <span className="text-xs font-bold text-[#374151] dark:text-slate-200">{c.type} proof {c.docName ? `— ${c.docName}` : ""}</span>
                  <label className="text-[11px] font-extrabold text-[#3D7E9E] cursor-pointer inline-flex items-center gap-1.5 hover:underline" data-testid={`cc-wiz-cred-doc-${c.type.replace(/\W/g, "-")}`}>
                    <Upload className="w-3.5 h-3.5" /> {c.docName ? "Replace" : "Upload"}
                    <input type="file" accept="image/*,application/pdf" className="hidden" onChange={async (e) => { const f = e.target.files?.[0]; e.target.value = ""; const r = await uploadVerificationDoc(f, "credential", c.type); if (r) setForm((prev) => ({ ...prev, credentials: prev.credentials.map((x) => (x.type === c.type ? { ...x, docName: f.name } : x)) })); }} />
                  </label>
                </div>
              ))}
            </div>
          )}
          <label className={`${ccBtnGhost} cursor-pointer inline-flex items-center gap-2`} data-testid="cc-wiz-id-upload">
            <Upload className="w-4 h-4 text-[#E5A0BC]" /> {form.idDocumentName ? `ID: ${form.idDocumentName}` : "Upload Government ID"}
            <input type="file" accept="image/*,application/pdf" className="hidden" onChange={async (e) => { const f = e.target.files?.[0]; e.target.value = ""; const r = await uploadVerificationDoc(f, "government_id"); if (r) setForm((prev) => ({ ...prev, idDocumentName: f.name })); }} />
          </label>
          <p className="text-[11px] text-[#9CA3AF]">Credentials are reviewed before a “Verified” badge appears on your profile.</p>
        </div>
      )}

      {step === 4 && (
        <div className="space-y-3" data-testid="cc-wiz-availability">
          <label className={ccLabel}>Weekly Availability (leave blank if unavailable)</label>
          {DAYS.map((d) => (
            <div key={d} className="flex items-center gap-3">
              <span className="text-xs font-bold text-[#6B7280] w-24 dark:text-slate-300">{d}</span>
              <input
                type="text"
                placeholder="e.g. 4 PM–10 PM"
                value={form.availability[d] || ""}
                onChange={(e) => setForm({ ...form, availability: { ...form.availability, [d]: e.target.value } })}
                className={ccInput}
                data-testid={`cc-wiz-avail-${d}`}
              />
            </div>
          ))}
        </div>
      )}

      {step === 5 && (
        <div className="space-y-4">
          <div>
            <label className={ccLabel}>ZIP Code</label>
            <input type="text" value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} className={ccInput} data-testid="cc-wiz-area-zip" />
          </div>
          <div>
            <label className={ccLabel}>Service Radius</label>
            <select value={form.radiusMiles} onChange={(e) => setForm({ ...form, radiusMiles: parseInt(e.target.value) })} className={ccInput} data-testid="cc-wiz-radius">
              {[5, 10, 15, 25, 50].map((r) => (
                <option key={r} value={r}>
                  Within {r} miles
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {step === 6 && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={ccLabel}>Hourly Rate ($)</label>
            <input type="number" min="0" value={form.hourlyRate} onChange={(e) => setForm({ ...form, hourlyRate: parseInt(e.target.value) || 0 })} className={ccInput} data-testid="cc-wiz-rate" />
          </div>
          <div>
            <label className={ccLabel}>Max Rate ($)</label>
            <input type="number" min="0" value={form.rateMax} onChange={(e) => setForm({ ...form, rateMax: parseInt(e.target.value) || 0 })} className={ccInput} data-testid="cc-wiz-rate-max" />
          </div>
        </div>
      )}

      {step === 7 && (
        <div className="p-5 bg-[#FFF9F2] border border-[#FCE6E9] rounded-3xl dark:bg-[#273244] dark:border-[#374457]" data-testid="cc-wiz-preview">
          <div className="flex items-center gap-4">
            <Avatar src={form.photo} name={form.name} size="w-16 h-16" />
            <div>
              <p className="text-base font-extrabold font-heading text-[#374151] dark:text-slate-100">{form.name || "Your Name"}</p>
              <p className="text-xs text-[#9CA3AF]">{form.city || "City"} · within {form.radiusMiles} miles</p>
              <p className="text-xs font-bold text-[#374151] mt-1 dark:text-slate-200">
                ${form.hourlyRate}–${form.rateMax}/hr · {form.yearsExperience} yrs
              </p>
            </div>
          </div>
          <p className="text-xs text-[#6B7280] mt-3 dark:text-slate-300">{form.bio || "Your bio will appear here."}</p>
          <div className="flex gap-1.5 mt-3 flex-wrap">
            {form.services.map((s) => (
              <span key={s} className="px-2 py-0.5 bg-[#E6F2FA] text-[#3D7E9E] text-[10px] font-bold rounded-full">
                {s}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-between mt-8 pt-4 border-t border-[#FCE6E9] dark:border-[#374457]">
        <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} className={`${ccBtnGhost} disabled:opacity-40`} data-testid="cc-wiz-back">
          <ChevronLeft className="w-4 h-4 inline -mt-0.5" /> Back
        </button>
        {step < 7 ? (
          <button
            onClick={() => {
              if (step === 0 && !form.name.trim()) {
                toast.error("Please enter your name before continuing.");
                return;
              }
              if (step === 1 && form.services.length === 0) {
                toast.error("Select at least one service you offer.");
                return;
              }
              setStep((s) => s + 1);
            }}
            className={ccBtnBlue}
            data-testid="cc-wiz-next"
          >
            Next <ChevronRight className="w-4 h-4 inline -mt-0.5" />
          </button>
        ) : (
          <button onClick={publish} className={ccBtnPink} data-testid="cc-wiz-publish">
            Publish My Profile
          </button>
        )}
      </div>
    </div>
  );
};

// ---------- Verification Center ----------
const VerificationCenter = ({ ctx }) => {
  const [data, setData] = useState(null);
  const load = () => {
    if (ctx.currentUser?.role !== "caregiver") return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/caregiver/profile`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setData(d))
      .catch(() => {});
  };
  useEffect(() => { load(); }, [ctx.currentUser?.role]);

  const docs = data?.docs || [];
  const claimed = data?.required_credentials || [];
  const docFor = (type, cred = "") => docs.find((d) => d.doc_type === type && (type === "government_id" || d.credential_type === cred));
  const overall = data?.verification_status === "verified";

  const rows = [
    { key: "gov", label: "Government ID", doc: docFor("government_id"), uploadType: "government_id", cred: "" },
    ...claimed.map((c) => ({ key: `cred-${c}`, label: `${c} License/Certificate`, doc: docFor("credential", c), uploadType: "credential", cred: c })),
  ];

  return (
    <div className={`${ccCard} p-8 max-w-2xl animate-fade-up`} data-testid="cc-verification">
      <div className="flex items-center justify-between gap-3 mb-1">
        <h2 className="text-xl font-black font-heading text-[#374151] dark:text-slate-100 flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-[#5CB3DC]" /> Verification Center
        </h2>
        {data && (
          <span data-testid="cc-verification-overall" className={`text-[11px] font-extrabold px-3 py-1.5 rounded-full ${overall ? "bg-[#E2F4E1] text-[#2F6031]" : "bg-[#F5E3B3]/40 text-[#8a6d1a]"}`}>
            {overall ? "Verified" : "Unverified"}
          </span>
        )}
      </div>
      <p className="text-xs text-[#9CA3AF] font-medium mb-6">
        Your profile stays <b>Unverified</b> until your government ID and every claimed license/credential have been reviewed and approved by a NestTrack master admin.
      </p>
      <div className="overflow-hidden rounded-2xl border border-[#FCE6E9] dark:border-[#374457]" data-testid="cc-verification-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#FFF9F2] dark:bg-[#273244]">
              <th className="text-left px-4 py-3 text-xs font-extrabold uppercase tracking-wider text-[#9CA3AF]">Document</th>
              <th className="text-left px-4 py-3 text-xs font-extrabold uppercase tracking-wider text-[#9CA3AF]">Status</th>
              <th className="text-right px-4 py-3 text-xs font-extrabold uppercase tracking-wider text-[#9CA3AF]">File</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className="border-t border-[#FCE6E9] dark:border-[#374457]" data-testid={`cc-verify-row-${r.label.replace(/\W/g, "-")}`}>
                <td className="px-4 py-3 text-sm font-bold text-[#374151] dark:text-slate-200">{r.label}</td>
                <td className="px-4 py-3">
                  {r.doc?.verified ? (
                    <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[#2F6031]">
                      <CheckCircle2 className="w-4 h-4" /> Verified
                    </span>
                  ) : r.doc ? (
                    <span className="text-xs font-bold text-[#8a6d1a]">Pending review</span>
                  ) : (
                    <span className="text-xs font-semibold text-[#9CA3AF]">Not submitted</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <label className="text-[11px] font-extrabold text-[#3D7E9E] cursor-pointer inline-flex items-center gap-1.5 hover:underline" data-testid={`cc-verify-upload-${r.key}`}>
                    <Upload className="w-3.5 h-3.5" /> {r.doc ? "Replace" : "Upload"}
                    <input type="file" accept="image/*,application/pdf" className="hidden" onChange={async (e) => { const f = e.target.files?.[0]; e.target.value = ""; const res = await uploadVerificationDoc(f, r.uploadType, r.cred || ""); if (res) load(); }} />
                  </label>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-[#9CA3AF] leading-relaxed mt-5 p-4 bg-[#FFF9F2] border border-[#F5E3B3] rounded-2xl dark:bg-[#273244]" data-testid="cc-verification-disclaimer">
        {VERIFICATION_DISCLAIMER}
      </p>
    </div>
  );
};

// ---------- Availability Calendar ----------
const AvailabilityCalendar = ({ ctx }) => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const first = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startPad = first.getDay();
  const monthName = now.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const cells = [...Array(startPad).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

  const styleFor = (day) => {
    const key = `${year}-${month + 1}-${day}`;
    const st = ctx.availabilityDays[key];
    if (st === "available") return "bg-[#E2F4E1] border-[#C4E5C2] text-[#2F6031]";
    if (st === "unavailable") return "bg-rose-50 border-rose-200 text-rose-400";
    return "bg-[#FFF9F2] border-[#FCE6E9] text-[#6B7280] hover:border-[#B3E0F2] dark:bg-[#273244] dark:border-[#374457] dark:text-slate-300";
  };

  return (
    <div className={`${ccCard} p-8 max-w-2xl animate-fade-up`} data-testid="cc-availability">
      <h2 className="text-xl font-black font-heading text-[#374151] dark:text-slate-100 mb-1">My Availability</h2>
      <p className="text-xs text-[#9CA3AF] font-medium mb-4">Click a date to cycle: Available → Unavailable → Clear. This feeds directly into search.</p>
      <div className="flex gap-4 mb-4 text-[11px] font-bold">
        <span className="flex items-center gap-1.5 text-[#2F6031]"><span className="w-2.5 h-2.5 rounded-full bg-[#7CC98F]" /> Available</span>
        <span className="flex items-center gap-1.5 text-rose-400"><span className="w-2.5 h-2.5 rounded-full bg-rose-300" /> Unavailable</span>
        <span className="flex items-center gap-1.5 text-[#D9A93E]"><span className="w-2.5 h-2.5 rounded-full bg-[#F5C445]" /> Pending Request</span>
        <span className="flex items-center gap-1.5 text-[#3D7E9E]"><span className="w-2.5 h-2.5 rounded-full bg-[#7CC3E5]" /> Booked</span>
      </div>
      <p className="text-sm font-extrabold font-heading text-[#374151] dark:text-slate-100 mb-3">{monthName}</p>
      <div className="grid grid-cols-7 gap-1.5 text-center text-[10px] font-extrabold uppercase text-[#9CA3AF] mb-1.5">
        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
          <span key={d}>{d}</span>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1.5" data-testid="cc-calendar-grid">
        {cells.map((day, i) =>
          day ? (
            <button
              key={`${year}-${month + 1}-${day}`}
              onClick={() => ctx.toggleAvailabilityDay(`${year}-${month + 1}-${day}`)}
              data-testid={`cc-cal-day-${day}`}
              className={`aspect-square rounded-xl border text-xs font-bold transition-all duration-150 ${styleFor(day)}`}
            >
              {day}
            </button>
          ) : (
            <span key={`empty-${i}`} />
          )
        )}
      </div>
    </div>
  );
};

// ---------- Reviews ----------
const ReviewsScreen = ({ ctx }) => {
  const completed = ctx.requests.filter((r) => r.status === "completed");
  const [target, setTarget] = useState(completed[0]?.id || "");
  const [rating, setRating] = useState(5);
  const [wouldAgain, setWouldAgain] = useState(true);
  const [text, setText] = useState("");

  const myReviews = ctx.viewUser.role === "caregiver" ? ctx.reviews.filter((r) => r.caregiverId === ctx.viewUser.caregiverId) : ctx.reviews;

  const submit = (e) => {
    e.preventDefault();
    const req = ctx.requests.find((r) => r.id === target);
    if (!req) return;
    ctx.addReview({
      caregiverId: req.caregiverId,
      reviewer: ctx.viewUser.name,
      rating,
      text,
      wouldReconnect: wouldAgain,
      date: new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
    });
    ctx.advanceRequest(req.id, "reviewed");
    setText("");
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="cc-reviews">
      {ctx.viewUser.role !== "caregiver" && completed.length > 0 && (
        <form onSubmit={submit} className={`${ccCard} p-6 space-y-4`} data-testid="cc-review-form">
          <h3 className="text-sm font-extrabold font-heading text-[#374151] dark:text-slate-100">Rate Your Experience</h3>
          <div>
            <label className={ccLabel}>Caregiver (completed connections)</label>
            <select value={target} onChange={(e) => setTarget(e.target.value)} className={ccInput} data-testid="cc-review-target">
              {completed.map((r) => {
                const cg = ctx.caregivers.find((c) => c.id === r.caregiverId);
                return (
                  <option key={r.id} value={r.id}>
                    {cg?.name} — {r.service} ({r.date})
                  </option>
                );
              })}
            </select>
          </div>
          <div className="flex items-center gap-1.5" data-testid="cc-review-stars">
            {[1, 2, 3, 4, 5].map((i) => (
              <button key={i} type="button" onClick={() => setRating(i)} data-testid={`cc-review-star-${i}`}>
                <Star className={`w-7 h-7 transition-colors ${i <= rating ? "text-[#F5C445] fill-[#F5C445]" : "text-[#E5E7EB] fill-[#E5E7EB]"}`} />
              </button>
            ))}
          </div>
          <div>
            <label className={ccLabel}>Would you connect with this caregiver again?</label>
            <div className="flex gap-2">
              {[true, false].map((v) => (
                <button
                  key={String(v)}
                  type="button"
                  onClick={() => setWouldAgain(v)}
                  data-testid={`cc-review-again-${v}`}
                  className={`px-4 py-2 rounded-full text-xs font-bold border transition-all ${
                    wouldAgain === v ? "bg-[#E2F4E1] border-[#C4E5C2] text-[#2F6031]" : "bg-[#FFF9F2] border-[#FCE6E9] text-[#9CA3AF] dark:bg-[#273244] dark:border-[#374457]"
                  }`}
                >
                  {v ? "Yes" : "No"}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className={ccLabel}>What did you like? (optional)</label>
            <textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} className={ccInput} data-testid="cc-review-text" />
          </div>
          <button type="submit" className={ccBtnPink} data-testid="cc-review-submit">
            Submit Review
          </button>
        </form>
      )}

      <div className={`${ccCard} p-6`}>
        <h3 className="text-sm font-extrabold font-heading text-[#374151] dark:text-slate-100 mb-4">
          {ctx.viewUser.role === "caregiver" ? "Reviews I've Received" : "Reviews"}
        </h3>
        {myReviews.length === 0 ? (
          <p className="text-xs text-[#9CA3AF]">No reviews yet.</p>
        ) : (
          <div className="space-y-3">
            {myReviews.map((r) => {
              const cg = ctx.caregivers.find((c) => c.id === r.caregiverId);
              return (
                <div key={r.id} className="p-4 bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl dark:bg-[#273244] dark:border-[#374457]" data-testid={`cc-myreview-${r.id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <span className="text-xs font-extrabold text-[#374151] dark:text-slate-100">
                      {r.reviewer} → {cg?.name || "Caregiver"}
                    </span>
                    <Stars rating={r.rating} size="w-3.5 h-3.5" />
                  </div>
                  {r.text && <p className="text-sm text-[#6B7280] mt-1.5 dark:text-slate-300">“{r.text}”</p>}
                  <p className="text-[10px] text-[#9CA3AF] mt-1">
                    {r.date} · Would connect again: {r.wouldReconnect ? "Yes" : "No"}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
